import { SearchIcon, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import type { RxDiagnosis } from '@/entities/rx';
import { useSearchDiagnosesQuery } from '@/entities/rx';
import { Button } from '@/shared/ui/shadcn/button';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/shared/ui/shadcn/command';
import { Input } from '@/shared/ui/shadcn/input';

import type { RxFormApi } from '../../model/types';

interface Props {
  form: RxFormApi;
}

interface DiagnosisRow {
  diagnosisId: number;
  code: string;
  name: string;
}

export function DiagnosisSelector({ form }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const trimmed = searchText.trim();
  const searchQuery = useSearchDiagnosesQuery(trimmed.length >= 2 ? trimmed : undefined);

  const rows = useMemo((): DiagnosisRow[] => {
    return (searchQuery.data ?? [])
      .filter((r) => r.DiagnosisId != null && r.DiagnosisCode)
      .map((r) => ({
        diagnosisId: r.DiagnosisId!,
        code: r.DiagnosisCode!,
        name: r.DiagnosisName ?? '',
      }));
  }, [searchQuery.data]);

  const selected = form.state.values.selectedDiagnosis as RxDiagnosis | null;
  const displayLabel = selected ? `${selected.code} — ${selected.name}` : null;

  const handleSelect = (row: DiagnosisRow) => {
    form.setFieldValue('selectedDiagnosis', {
      diagnosisId: row.diagnosisId,
      code: row.code,
      name: row.name,
    });
    setIsOpen(false);
    setSearchText('');
  };

  const handleClear = () => {
    form.setFieldValue('selectedDiagnosis', null);
  };

  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">Diagnosis (ICD-10)</label>
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <SearchIcon className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
          <Input
            readOnly
            value={displayLabel ?? ''}
            placeholder="Search diagnoses…"
            onClick={() => setIsOpen(true)}
            onFocus={() => setIsOpen(true)}
            className="cursor-pointer pl-9"
          />
        </div>
        {selected ? (
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            onClick={handleClear}
            aria-label="Clear diagnosis"
          >
            <X className="size-4" aria-hidden />
          </Button>
        ) : null}
      </div>

      <CommandDialog
        open={isOpen}
        onOpenChange={(nextOpen) => {
          setIsOpen(nextOpen);
          if (!nextOpen) setSearchText('');
        }}
        title="Search diagnoses"
        description="Search by ICD-10 code or description."
      >
        <CommandInput
          value={searchText}
          onValueChange={setSearchText}
          placeholder="e.g. G20 or Parkinson"
          autoFocus
        />
        <CommandList>
          {trimmed.length === 0 ? (
            <CommandEmpty>Type a code or description to search.</CommandEmpty>
          ) : trimmed.length < 2 ? (
            <CommandEmpty>Type at least 2 characters.</CommandEmpty>
          ) : searchQuery.isPending ? (
            <CommandEmpty>Searching…</CommandEmpty>
          ) : searchQuery.isError ? (
            <CommandEmpty>
              Search failed.
              <div className="mt-3 flex justify-center">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => void searchQuery.refetch()}
                >
                  Retry
                </Button>
              </div>
            </CommandEmpty>
          ) : rows.length === 0 ? (
            <CommandEmpty>No diagnoses found.</CommandEmpty>
          ) : (
            <CommandGroup heading="Results">
              {rows.map((row) => (
                <CommandItem
                  key={row.diagnosisId}
                  value={`${row.code} ${row.name} ${row.diagnosisId}`}
                  onSelect={() => handleSelect(row)}
                >
                  <div className="flex min-w-0 flex-col">
                    <div className="truncate font-medium">
                      {row.code}
                      {row.name ? ` — ${row.name}` : ''}
                    </div>
                    <div className="text-muted-foreground font-mono text-[10px]">
                      ID {row.diagnosisId}
                    </div>
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
        </CommandList>
      </CommandDialog>
    </div>
  );
}
